import { type FC, useMemo } from 'react';
import {
  ProtoActiveCard,
  ProtoBottomRow,
  ProtoCardTitle,
  ProtoDateLabel,
  ProtoMeta,
  ProtoPrimaryButton,
  ProtoTopRow,
} from '../TrainingCard';
import Play from '../../../../assets/PlayPolygon.svg';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { useLanguageContext } from '../../../../lib/hooks/useLanguageContext';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { CardStatus } from '../../enums/CardStatus';
import { getCardStatus } from '../../utils/getCardStatus';
import type { EnrolledCampaignListResponse } from '../../../../apiHooks/useCampaignsQuery';

type MandatoryTrainingCardProps = {
  campaign: EnrolledCampaignListResponse;
};

export const MandatoryTrainingCard: FC<MandatoryTrainingCardProps> = ({ campaign }: MandatoryTrainingCardProps) => {
  const { dueDate, campaignUrl, name, modulesProgress, modulesTotal, learnerStatus } = campaign;
  const dd = useMemo(() => new Date(dueDate), [dueDate]);
  const cardStatus: CardStatus = getCardStatus(dd);
  const isUrgent = cardStatus === CardStatus.urgent;
  const [langCode] = useLanguageContext();

  const buttonText =
    learnerStatus === 'inProgress' ? (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="resume" />
    ) : (
      <NamedContentEntryText container="span" refKey="yourTraining" subKey="startNow" />
    );

  return (
    <ProtoActiveCard>
      <ProtoTopRow>
        <ProtoCardTitle>{name}</ProtoCardTitle>
        <ProtoDateLabel $urgent={isUrgent}>
          <Icon icon={IconType.CALENDAR} width={12} height={12} />
          {dd.toLocaleString(langCode, { month: 'short', day: 'numeric' })}
        </ProtoDateLabel>
      </ProtoTopRow>
      <ProtoBottomRow>
        <ProtoMeta>
          <Icon icon={IconType.CLIPBOARD_CHECK} width={12} height={12} />
          {modulesProgress} / {modulesTotal}
        </ProtoMeta>
        <ProtoPrimaryButton
          disabled={!campaignUrl}
          onClick={() => {
            if (campaignUrl) {
              window.open(campaignUrl, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          <Play className="rtl-flip" />
          {buttonText}
        </ProtoPrimaryButton>
      </ProtoBottomRow>
    </ProtoActiveCard>
  );
};
